import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';
import type { ApiResponse, ApiError } from '@/types';

// API客户端配置
// 前端统一走同源 /api：本地由 Vite proxy 转发，线上由 Nginx 转发。
// 不读取 VITE_API_BASE_URL，避免把本机后端地址烤进生产静态包。
const API_BASE_URL = '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        const headers = config.headers as any;
        const skipAuth = Boolean(headers?.['X-Skip-Auth']);
        if (skipAuth && headers) {
          delete headers['X-Skip-Auth'];
        }

        // 添加认证token
        const token = this.getAuthToken();
        if (!skipAuth && token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 添加请求ID用于追踪
        config.headers['X-Request-ID'] = this.generateRequestId();

        // 开发环境下打印请求信息
        if (import.meta.env.DEV) {
          console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // 开发环境下打印响应信息
        if (import.meta.env.DEV) {
          console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          });
        }

        return response;
      },
      (error: AxiosError) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleResponseError(error: AxiosError) {
    const { response, request, message } = error;

    if (response) {
      // 服务器返回错误状态码
      const { status, data } = response;
      
      switch (status) {
        case 401:
          this.handleUnauthorized();
          break;
        case 403:
          toast.error('没有权限访问该资源');
          break;
        case 404:
          toast.error('请求的资源不存在');
          break;
        case 422:
          this.handleValidationError(data as any);
          break;
        case 429:
          toast.error('请求过于频繁，请稍后再试');
          break;
        case 500:
          toast.error('服务器内部错误，请稍后再试');
          break;
        default:
          const errorMessage = (data as any)?.message || `请求失败 (${status})`;
          toast.error(errorMessage);
      }
    } else if (request) {
      // 网络错误
      toast.error('网络连接失败，请检查网络设置');
    } else {
      // 其他错误
      toast.error(message || '请求失败');
    }

    console.error('❌ API Error:', error);
  }

  private handleUnauthorized() {
    // 清除认证信息
    this.clearAuthToken();
    
    // 显示错误提示
    toast.error('登录已过期，请重新登录');
    
    // 跳转到登录页面
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private handleValidationError(data: any) {
    if (data?.errors && Array.isArray(data.errors)) {
      data.errors.forEach((error: ApiError) => {
        toast.error(error.message);
      });
    } else if (data?.message) {
      toast.error(data.message);
    } else {
      toast.error('请求参数验证失败');
    }
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private clearAuthToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private unwrapResponse<T>(payload: unknown): T {
    if (payload && typeof payload === 'object' && 'data' in (payload as any)) {
      const maybeApiResponse = payload as ApiResponse<T>;
      if ('success' in (payload as any)) {
        return (maybeApiResponse.data ?? (payload as any)) as T;
      }
      return (maybeApiResponse as any).data as T;
    }

    return payload as T;
  }

  // 公共请求方法
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.client.get(url, { params });
    return this.unwrapResponse<T>(response.data);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config);
    return this.unwrapResponse<T>(response.data);
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    return this.unwrapResponse<T>(response.data);
  }

  async delete<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.delete(url, { data });
    return this.unwrapResponse<T>(response.data);
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch(url, data);
    return this.unwrapResponse<T>(response.data);
  }

  // 文件上传
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return this.unwrapResponse<T>(response.data);
  }

  // 下载文件
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // 健康检查
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health');
      return true;
    } catch {
      return false;
    }
  }

  // 设置认证token
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  // 获取客户端实例（用于特殊情况）
  getClient(): AxiosInstance {
    return this.client;
  }
}

// 创建全局API客户端实例
export const apiClient = new ApiClient();

// 导出类型和实例
export type { ApiClient };
export default apiClient;
